import * as M from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';

import {
  AS_CS_RECEIPT_STATUS_ALL,
  AS_CS_RECEIPT_STATUS_FILTER_OPTIONS,
} from '../../../../../../../shared/constants/asCsOptions';

import { InfoField } from '../../../../../../ui/infoField/InfoField';
import CustomSearch from '../../../../../../ui/search/customSearch';

import type { KeyboardEvent, MouseEvent, RefObject } from 'react';
import type { AiAgentAsCsSearchType, IAiAgentAsCsFilterForm } from '../types';
import type { IModelOption } from '../../../Common/hooks/useModelOptions';

type AsCsReceiptFilterValue = (typeof AS_CS_RECEIPT_STATUS_FILTER_OPTIONS)[number]['value'];

interface IAiAgentAsCsSectionProps {
  filterForm: IAiAgentAsCsFilterForm;
  searchType: AiAgentAsCsSearchType;
  inputRef: RefObject<HTMLInputElement>;
  modelOptions: IModelOption[];
  onChangeFilterForm: (nextFilter: Partial<IAiAgentAsCsFilterForm>) => void;
  onChangeSearchType: (value: string | null) => void;
  handleSearch: (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLInputElement>) => void;
  resetSearchInput: () => void;
  onReset: () => void;
}

export default function AiAgentAsCsFilterSection(props: IAiAgentAsCsSectionProps) {
  const {
    filterForm,
    modelOptions,
    searchType,
    inputRef,
    onChangeFilterForm,
    onChangeSearchType,
    handleSearch,
    resetSearchInput,
    onReset,
  } = props;

  const handleReceiptStatusChange = (value: AsCsReceiptFilterValue) => {
    if (value === AS_CS_RECEIPT_STATUS_ALL) {
      onChangeFilterForm({ receiptStatuses: [] });
      return;
    }

    const receiptStatuses = filterForm.receiptStatuses.includes(value)
      ? filterForm.receiptStatuses.filter((status) => status !== value)
      : [...filterForm.receiptStatuses, value];

    onChangeFilterForm({ receiptStatuses });
  };

  return (
    <M.Box
      style={{
        borderTop: '1px solid var(--mantine-color-gray-3)',
        borderLeft: '1px solid var(--mantine-color-gray-3)',
      }}
    >
      <M.SimpleGrid cols={2} spacing={0} verticalSpacing={0} style={{ gridTemplateColumns: '180px minmax(0, 1fr)' }}>
        <InfoField label="접수일자" labelAlign="left" contentAlign="left">
          <M.Flex gap="xs" align="center" wrap="wrap">
            <DatePickerInput
              leftSection={<IconCalendar size={14} />}
              placeholder="YYYY-MM-DD"
              valueFormat="YYYY-MM-DD"
              w={210}
              value={filterForm.startDate}
              onChange={(date) => onChangeFilterForm({ startDate: date })}
            />
            <M.Text c="gray.7">-</M.Text>
            <DatePickerInput
              leftSection={<IconCalendar size={14} />}
              placeholder="YYYY-MM-DD"
              valueFormat="YYYY-MM-DD"
              w={210}
              value={filterForm.endDate}
              onChange={(date) => onChangeFilterForm({ endDate: date })}
            />
          </M.Flex>
        </InfoField>
        <InfoField label="진행 상태" labelAlign="left" contentAlign="left">
          <M.Flex gap="lg" wrap="wrap">
            {AS_CS_RECEIPT_STATUS_FILTER_OPTIONS.map((option) => (
              <M.Checkbox
                key={option.value}
                label={option.label}
                size="sm"
                checked={
                  option.value === AS_CS_RECEIPT_STATUS_ALL
                    ? filterForm.receiptStatuses.length === 0
                    : filterForm.receiptStatuses.includes(option.value)
                }
                onChange={() => handleReceiptStatusChange(option.value)}
              />
            ))}
          </M.Flex>
        </InfoField>
        <InfoField label="기계모델" labelAlign="left" contentAlign="left">
          <M.Select
            searchable
            placeholder="모델을 선택해주세요."
            value={filterForm.modelId}
            onChange={(modelId) => onChangeFilterForm({ modelId })}
            data={modelOptions}
          />
        </InfoField>
        <InfoField label="상세 검색" labelAlign="left" contentAlign="left">
          <M.Flex gap="sm" align="center" wrap="wrap" w="100%">
            <CustomSearch
              resetSearch={onReset}
              resetSearchInput={resetSearchInput}
              searchType={searchType}
              setSearchType={onChangeSearchType}
              inputRef={inputRef}
              handleSearch={handleSearch}
              searchOptions={[
                {
                  value: 'serialNumber',
                  label: '출고장비 S/N',
                },
                {
                  value: 'customerName',
                  label: '거래처명',
                },
                {
                  value: 'manageNo',
                  label: '관리번호',
                },
                {
                  value: 'receiptNo',
                  label: '접수번호',
                },
                {
                  value: 'receiptContent',
                  label: '접수내용',
                },
              ]}
              placeholder="검색어를 입력하세요."
            />
          </M.Flex>
        </InfoField>
      </M.SimpleGrid>
    </M.Box>
  );
}
